<template>
  <div>
    <a-list item-layout="vertical" size="large" :data-source="listData" class="performance-list mb-120">
      <a-list-item slot="renderItem" key="item.title" slot-scope="item, index">
        <img
          slot="extra"
          alt="illustration"
          :src="item.image"
          class="performance-item-img clickable-img"
          @click="handlePreview(item.image)"
        />
        <a-list-item-meta :description="item.description">
          <div slot="title" class="font-web performance-item-title-wrapper">
            <span class="performance-item-title">{{ item.title }}</span>
          </div>
        </a-list-item-meta>
        <div class="performance-item-content">
          <div class="content-section mb-10">
            <h4 class="font-web section-label">รายละเอียด / Description</h4>
            <p class="font-web mt-0 mb-0">{{ item.content }}</p>
          </div>
          
          <!-- Document Table for Support Staff -->
          <div v-if="item.title.includes('พนักงานสายสนับสนุน')" class="mt-20">
            <a-spin :spinning="loading">
              <table class="doc-table">
                <thead>
                  <tr>
                    <th>รายการไฟล์ / File List</th>
                    <th class="text-center">ไทย (TH)</th>
                    <th class="text-center">Eng (EN)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="doc in supportStaffDocs" :key="doc.thName">
                    <td>
                      <div class="file-name-main">{{ doc.thName }}</div>
                      <div v-if="doc.enName" class="file-name-sub">({{ doc.enName }})</div>
                      <div class="file-date">อัปเดตเมื่อ: {{ formatDate(doc.updatedAt) }}</div>
                    </td>
                    <td class="text-center">
                      <a v-if="doc.th" :href="doc.th.doc_file" target="_blank" class="download-link">
                        <a-icon type="download" />
                      </a>
                      <span v-else>-</span>
                    </td>
                    <td class="text-center">
                      <a v-if="doc.en" :href="doc.en.doc_file" target="_blank" class="download-link">
                        <a-icon type="download" />
                      </a>
                      <span v-else>-</span>
                    </td>
                  </tr>
                  <tr v-if="!loading && supportStaffDocs.length === 0">
                    <td colspan="3" class="text-center">ไม่พบข้อมูล / No data found</td>
                  </tr>
                </tbody>
              </table>
            </a-spin>
          </div>

          <!-- Document Table for Academic Staff -->
          <div v-if="item.title.includes('Academic Staff')" class="mt-20">
            <a-spin :spinning="loading">
              <table class="doc-table">
                <thead>
                  <tr>
                    <th>รายการไฟล์ / File List</th>
                    <th class="text-center">Eng (EN)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="doc in academicStaffDocs" :key="doc.doc.id">
                    <td>
                      <div class="file-name-main">{{ doc.displayName }}</div>
                      <div class="file-date">อัปเดตเมื่อ: {{ formatDate(doc.updatedAt) }}</div>
                    </td>
                    <td class="text-center">
                      <a :href="doc.doc.doc_file" target="_blank" class="download-link">
                        <a-icon type="download" />
                      </a>
                    </td>
                  </tr>
                  <tr v-if="!loading && academicStaffDocs.length === 0">
                    <td colspan="2" class="text-center">ไม่พบข้อมูล / No data found</td>
                  </tr>
                </tbody>
              </table>
            </a-spin>
          </div>

          <div class="mt-15"></div>
        </div>
        <template slot="actions"></template>
      </a-list-item>
    </a-list>

    <!-- Image Preview Modal -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancelPreview" width="80%">
      <img alt="preview" style="width: 100%" :src="previewImage" />
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";

export default {
  data() {
    return {
      loading: false,
      docs: [],
      previewVisible: false,
      previewImage: '',
      listData: [
        {
          title: '1. กำหนดการและลำดับขั้นตอนการประเมิน (พนักงานสายสนับสนุน)',
          description: 'Performance Appraisal Timeline (Support Staff)',
          content: 'เพื่อให้บุคลากรสามารถวางแผนและเตรียมความพร้อมในการสรุปผลงานและสมรรถนะ',
          image: '/images/hri_dashboard/2026_PerformanceSchedule_Support.png',
          href: 'javascript:;',
          thLink: 'https://muic.mahidol.ac.th/th/performance-management/',
          enLink: 'https://muic.mahidol.ac.th/en/performance-management/'
        },
        {
          title: '2. Performance Appraisal Timeline (Academic Staff)',
          description: 'กำหนดการและลำดับขั้นตอนการประเมิน (พนักงานสายวิชาการ)',
          content: 'Steps and timeline guide to the annual performance appraisal process for academic staff Documents for Performance Appraisal',
          image: '/images/hri_dashboard/2026_PerformanceStaff.png',
          href: 'javascript:;',
          thLink: 'https://muic.mahidol.ac.th/th/performance-management-manual/',
          enLink: 'https://muic.mahidol.ac.th/en/performance-management-manual/'
        },
      ]
    };
  },
  computed: {
    supportStaffDocs() {
      // Filter only form docs
      const formDocs = this.docs.filter(d => d.doc_type === 'form');
      
      // Helper to extract form key (e.g., "Form 0.4")
      const getFormKey = (name) => {
        const match = name.match(/^Form\s+\d+\.\d+/);
        return match ? match[0] : name;
      };

      // Filter Thai files for Support Staff
      const thFiles = formDocs.filter(d => d.file_name.endsWith('_[SupportStaff]_T'));
      // Filter English files for Support Staff
      const enFiles = formDocs.filter(d => d.file_name.endsWith('_[SupportStaff]_E'));

      return thFiles.map(th => {
        const key = getFormKey(th.file_name);
        // Find corresponding English file by matching the form key
        const en = enFiles.find(e => getFormKey(e.file_name) === key);
        
        return {
          thName: th.file_name.replace('_[SupportStaff]_T', ''),
          enName: en ? en.file_name.replace('_[SupportStaff]_E', '') : '',
          updatedAt: th.updated_at,
          th,
          en
        };
      }).sort((a, b) => a.thName.localeCompare(b.thName));
    },
    academicStaffDocs() {
      // Filter only form docs
      const formDocs = this.docs.filter(d => d.doc_type === 'form');
      
      // Filter files for Academic Staff
      const academicFiles = formDocs.filter(d => d.file_name.includes('_[AcademicStaff]'));

      return academicFiles.map(doc => {
        return {
          displayName: doc.file_name.replace('_[AcademicStaff]', '').replace('_E', '').replace('_T', ''),
          updatedAt: doc.updated_at,
          doc
        };
      }).sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
  },
  mounted() {
    this.fetchDocs();
  },
  methods: {
    async fetchDocs() {
      this.loading = true;
      try {
        const res = await this.$http.get('/hri/doc-file/?doc_type=form');
        if (res.status === 200 && Array.isArray(res.data)) {
          this.docs = res.data;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching performance docs:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    handlePreview(image) {
      this.previewImage = image;
      this.previewVisible = true;
    },
    handleCancelPreview() {
      this.previewVisible = false;
    }
  }
};
</script>

<style scoped>
.performance-list {
  padding: 0 20px;
  margin-bottom: 200px;
}
.performance-item-title {
  font-size: 20px !important;
  font-weight: 800 !important;
  color: #1a1a2e !important;
  transition: color 0.3s ease;
}
.performance-item-title:hover {
  color: #4285F4 !important;
}
.performance-item-content {
  color: #595959;
  font-size: 14px;
  line-height: 1.6;
}
.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  margin-bottom: 5px;
  letter-spacing: 0.5px;
}
.performance-item-title-wrapper {
  margin-bottom: 4px;
}
.performance-item-img {
  width: 280px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  object-fit: cover;
  transition: transform 0.3s ease;
}
.clickable-img {
  cursor: pointer;
}
.clickable-img:hover {
  transform: scale(1.05) !important;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

/* Document Table Styles */
.doc-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.doc-table th {
  background: #1a1a2e;
  padding: 12px 15px;
  text-align: left;
  font-weight: 700;
  color: #ffffff;
  border-bottom: 2px solid #4285F4;
}
.doc-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  color: #434343;
}
.file-name-main {
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.4;
}
.file-name-sub {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
  font-style: italic;
}
.file-date {
  font-size: 11px;
  color: #bfbfbf;
  margin-top: 4px;
}
.doc-table tr:last-child td {
  border-bottom: none;
}
.text-center {
  text-align: center;
}
.download-link {
  color: #4285F4;
  font-size: 18px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.download-link:hover {
  color: #1a73e8;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .performance-list {
    padding: 0 10px;
  }
  .performance-item-title {
    font-size: 18px !important;
  }
  .performance-item-img {
    width: 100% !important;
    max-width: 320px;
    margin-top: 10px;
  }
  ::v-deep .ant-list-item-extra {
    margin-left: 0 !important;
    display: flex;
    justify-content: center;
  }
}
.action-btn {
  padding: 0;
  margin-right: 24px;
  font-weight: 600;
}
.mt-15 {
  margin-top: 15px;
}
.mt-20 {
  margin-top: 20px;
}
.performance-item-img:hover {
  transform: scale(1.02);
}
.action-btn:hover {
  color: #4285F4;
}
@media (max-width: 576px) {
  .action-btn {
    display: block;
    margin-bottom: 8px;
    margin-right: 0;
  }
}

/* Custom layout adjustments for Ant Design list-item actions */
::v-deep .ant-list-item-action {
  margin-top: 20px;
}
</style>
